import { useState, useEffect } from 'react';
import { Button, makeStyles, TextField, Typography } from '@material-ui/core';
import DatePicker from 'react-datepicker';
import '../../assets/datePickerCustomStyling.css';

import { CalendarItemType } from 'components/vacationPlanner/Calendar';
import { useGetCurrentUserQuery } from 'utils/wemble-api';
import {
    convertLocalToUTCDate,
    statusBackgroundColor,
    statusColor,
    formattedDateRange
} from 'components/Home/Forecast/utils';
import Modal from 'components/shared/Modal';

const useStyles = makeStyles((theme) => ({
    modal: {
        '& h2': {
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 24,
            color: theme.palette.text.primary,
        },
    },
    section: {
        marginBottom: 24,
        [theme.breakpoints.down('xs')]: {
            marginBottom: 16,
        },
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 500,
        marginBottom: 12,
        [theme.breakpoints.down('xs')]: {
            marginBottom: 6,
        },
        color: theme.palette.text.primary,
    },
    statusGrid: {
        display: 'flex',
        gap: 8,
        marginBottom: 16,
        flexWrap: 'wrap',
        [theme.breakpoints.down('xs')]: {
            gap: 6,
        },
    },
    statusOption: {
        padding: '8px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
        border: '2px solid transparent',
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
        [theme.breakpoints.down('xs')]: {
            padding: '6px 8px',
            borderRadius: 6,
        },
    },
    statusTitle: {
        fontSize: 14,
        fontWeight: 500,
        [theme.breakpoints.down('xs')]: {
            fontSize: 11,
            fontWeight: 400,
        },
    },
    statusOptionSelected: {
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    datePickerContainer: {
        '& .react-datepicker-wrapper': {
            width: '100%',
        },
    },
    datePickerSuggestionItem: {
        padding: 12,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        backgroundColor: '#F5F5F5',
        cursor: 'pointer',
        transition: '0.1s',
        '&:hover': {
            backgroundColor: '#E8E8E8',
            transform: 'scale(1.02)',
        },
    },
    datePickerText: {
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
        color: theme.palette.text.primary,
    },
    commentField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
        },
    },
    buttonContainer: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end',
        marginTop: 24,
        paddingTop: 24,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
    },
    button: {
        borderRadius: 8,
        padding: '10px 24px',
        fontSize: 14,
        fontWeight: 500,
        textTransform: 'none',
        boxShadow: 'none',
    },
    characterCount: {
        fontSize: 12,
        color: theme.palette.grey[600],
        textAlign: 'right',
        marginTop: 4,
    },
}));

const statusOptions = [
    { value: 'away', label: 'Away' },
    { value: '0', label: 'Available' },
    { value: '1', label: 'Some availability' },
    { value: '2', label: 'Busy' },
    { value: '3', label: 'Seriously busy' },
];

interface ForecastModalProps {
    isOpen: boolean;
    item: CalendarItemType | false;
    onClose: () => void;
    onSave?: (status: string, startDate: Date | null, endDate: Date | null, comment: string) => Promise<void>;
    onDelete?: (itemId: string) => Promise<void>;
    disabled?: boolean;
    dragDates?: { startDate: Date; endDate: Date } | null;
}

const ForecastModal = ({
    isOpen,
    item,
    onClose,
    onSave,
    onDelete,
    disabled = false,
    dragDates,
}: ForecastModalProps) => {
    const classes = useStyles();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [status, setStatus] = useState<string>('away');
    const [comment, setComment] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const { data: user } = useGetCurrentUserQuery();

    const maxCommentLength = 100;
    const isEditing = Boolean(item);

    useEffect(() => {
        if (item) {
            // Convert moment objects directly to Date objects
            // Since moment is already configured for UTC, we can use toDate() directly
            setStartDate(item.startDate.toDate());
            setEndDate(item.endDate?.toDate() || item.startDate.toDate());
            setStatus(item.status || 'away');
            setComment(item.comment || '');
        } else if (dragDates) {
            // Pre-populate with drag dates when creating new forecast
            // Convert to UTC to ensure consistency with date picker behavior
            setStartDate(convertLocalToUTCDate(dragDates.startDate));
            setEndDate(convertLocalToUTCDate(dragDates.endDate));
            setStatus('away');
            setComment('');
        } else {
            setStartDate(null);
            setEndDate(null);
            setStatus('away');
            setComment('');
        }
    }, [item, isOpen, dragDates]);

    const handleClose = () => {
        if (disabled || isLoading) return;
        setStartDate(null);
        setEndDate(null);
        setStatus('away');
        setComment('');
        onClose();
    };

    const handleSave = async () => {
        if (!startDate || isLoading || !onSave) return;

        setIsLoading(true);
        try {
            const finalEndDate = endDate || startDate;
            await onSave(status, startDate, finalEndDate, comment);
            handleClose();
        } catch (error) {
            console.error('Error saving forecast:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!item || !item._id || !onDelete || isLoading) return;

        setIsLoading(true);
        try {
            await onDelete(item._id);
            handleClose();
        } catch (error) {
            console.error('Error deleting forecast:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        // Convert local dates to UTC dates to ensure the selected date is preserved
        // regardless of timezone
        setStartDate(convertLocalToUTCDate(start));
        setEndDate(convertLocalToUTCDate(end));
    };

    const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newComment = event.target.value;
        if (newComment.length <= maxCommentLength) {
            setComment(newComment);
        }
    };

    const isWfhItem = item && item.wfhIndex != null;
    const canSave = startDate && !isLoading && !disabled;
    const isButtonDisabled = disabled || isLoading;

    return (
        <div style={{ position: 'relative', zIndex: isOpen ? 1060 : -1 }}>
            <Modal visible={isOpen} close={handleClose} customWidth={600} closeButton>
                <div className={classes.modal}>
                    {isWfhItem ? (
                        <>
                            <Typography variant="h2">Away</Typography>
                            <div className={classes.section}>
                                <Typography className={classes.sectionTitle}>
                                    {startDate && formattedDateRange({ startDate: startDate.toISOString(), endDate: startDate.toISOString() })}
                                </Typography>
                                <Typography style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                                    Set via WFH planner
                                </Typography>
                            </div>
                            <div className={classes.buttonContainer}>
                                <Button
                                    className={classes.button}
                                    variant="outlined"
                                    onClick={handleClose}
                                    disabled={isButtonDisabled}
                                >
                                    Cancel
                                </Button>
                                {onDelete && (
                                    <Button
                                        className={classes.button}
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleDelete}
                                        disabled={isButtonDisabled}
                                    >
                                        {isLoading ? 'Removing...' : 'Remove'}
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                    <Typography variant="h2">
                        {isEditing ? 'Edit Forecast' : 'Add Forecast'}
                    </Typography>

                    <div className={classes.section}>
                            <Typography className={classes.sectionTitle}>Status</Typography>
                            <div className={classes.statusGrid}>
                                {statusOptions.map((option) => (
                                    <div
                                        key={option.value}
                                        className={`${classes.statusOption} ${status === option.value ? classes.statusOptionSelected : ''
                                            }`}
                                        style={{
                                            backgroundColor: statusBackgroundColor(option.value),
                                            color: statusColor(option.value),
                                            ...(status === option.value && {
                                                borderColor: statusColor(option.value),
                                            }),
                                        }}
                                        onClick={() => setStatus(option.value)}
                                    >
                                        <Typography className={classes.statusTitle}>
                                            {option.label}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </div>

                    <div className={classes.section}>
                        <Typography className={classes.sectionTitle}>Date Range</Typography>
                        <div className={classes.datePickerContainer}>
                            <DatePicker
                                wrapperClassName="datePickerCustomStyling"
                                selectsRange
                                startDate={startDate}
                                endDate={endDate}
                                onChange={handleDateChange}
                                calendarStartDay={1}
                                formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
                                monthsShown={2}
                                placeholderText="Select date range"
                                customInput={
                                    <div className={classes.datePickerSuggestionItem}>
                                        <div className={classes.datePickerText}>
                                            {startDate && endDate
                                                ? formattedDateRange({ startDate: startDate.toISOString(), endDate: endDate.toISOString() })
                                                : startDate
                                                    ? formattedDateRange({ startDate: startDate.toISOString(), endDate: startDate.toISOString() })
                                                    : 'Select date range'
                                            }
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    </div>

                    <div className={classes.section}>
                        <Typography className={classes.sectionTitle}>Comment (Optional)</Typography>
                        <TextField
                            className={classes.commentField}
                            variant="outlined"
                            placeholder="Add a comment..."
                            value={comment}
                            onChange={handleCommentChange}
                            multiline
                            rows={2}
                            inputProps={{
                                maxLength: maxCommentLength,
                            }}
                        />
                        <Typography className={classes.characterCount}>
                            {comment.length}/{maxCommentLength}
                        </Typography>
                    </div>

                    <div className={classes.buttonContainer}>
                        <Button
                            className={classes.button}
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isButtonDisabled}
                        >
                            Cancel
                        </Button>

                        {isEditing && onDelete && (
                            <Button
                                className={classes.button}
                                variant="outlined"
                                color="secondary"
                                onClick={handleDelete}
                                disabled={isButtonDisabled}
                            >
                                Delete
                            </Button>
                        )}

                        {onSave && (
                        <Button
                            className={classes.button}
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={!canSave}
                        >
                            {isLoading ? 'Saving...' : (isEditing ? 'Update' : 'Add')}
                        </Button>
                        )}
                    </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ForecastModal; 